import { useState, useEffect } from "react";
import { Eye, X, Users } from "lucide-react";
import { api } from "../lib/api";

const FONT_STEPS = [90, 100, 110, 120];

const THEMES = [
  { id: "normal", label: "Default theme", cls: "bg-white text-slate-900 border-slate-300" },
  { id: "dark", label: "Dark / night theme", cls: "bg-slate-900 text-white border-slate-900" },
  { id: "contrast", label: "High contrast", cls: "bg-amber-400 text-slate-900 border-amber-500" },
  { id: "grayscale", label: "Grayscale", cls: "bg-fuchsia-600 text-white border-fuchsia-700" },
  { id: "cool", label: "Cool tone", cls: "bg-blue-600 text-white border-blue-700" },
];

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const date = `${DAYS[now.getDay()]}, ${now.getDate()} ${MONTHS[now.getMonth()]}-${now.getFullYear()}`;
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  return { date, time };
}

export default function AccessibilityBar() {
  const { date, time } = useClock();
  const [fontIdx, setFontIdx] = useState(1);
  const [theme, setTheme] = useState("normal");
  const [srOpen, setSrOpen] = useState(false);
  const [visitors, setVisitors] = useState(null);

  useEffect(() => { document.documentElement.style.fontSize = `${FONT_STEPS[fontIdx]}%`; }, [fontIdx]);
  useEffect(() => { document.documentElement.setAttribute("data-a11y", theme); }, [theme]);

  useEffect(() => {
    const seen = sessionStorage.getItem("kcc_visit_counted");
    const call = seen ? api.getVisitors() : api.hitVisitor();
    if (!seen) sessionStorage.setItem("kcc_visit_counted", "1");
    call.then((r) => setVisitors(r.data.count)).catch(() => {});
  }, []);

  const btn = "px-2 py-0.5 text-xs font-semibold rounded-sm transition-colors";
  const chip = "px-3 py-1 text-[11px] font-semibold rounded-sm bg-[#0a5c6d] hover:bg-[#08505f] transition-colors";

  return (
    <div data-testid="accessibility-bar" className="fixed top-0 inset-x-0 z-[60] bg-[#0e7490] text-white h-9 text-xs">
      <div className="max-w-[1600px] mx-auto h-full px-4 md:px-8 flex items-center justify-between gap-3">
        {/* Left: date/time + visitors */}
        <div className="flex items-center gap-4 min-w-0">
          <span data-testid="live-datetime" className="whitespace-nowrap font-medium">
            {date} <span className="text-white/70 tabular-nums">{time}</span>
          </span>
          <span data-testid="visitor-count" className="hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap text-white/85">
            <Users size={13} /> Visitors: <strong className="tabular-nums">{visitors == null ? "…" : visitors.toLocaleString()}</strong>
          </span>
        </div>

        {/* Right: toolbar */}
        <div className="flex items-center gap-2 md:gap-3">
          <a data-testid="skip-toolbar-link" href="#main-content" className={`${chip} hidden md:inline-block`}>Skip toolbar content</a>

          {/* Font size */}
          <div className="flex items-center gap-1" role="group" aria-label="Font size">
            <button data-testid="font-decrease" title="Decrease font size" onClick={() => setFontIdx((i) => Math.max(0, i - 1))} className={`${btn} bg-slate-500/70 hover:bg-slate-500`}>-A</button>
            <button data-testid="font-reset" title="Normal font size" onClick={() => setFontIdx(1)} className={`${btn} bg-slate-500/70 hover:bg-slate-500`}>A</button>
            <button data-testid="font-increase" title="Increase font size" onClick={() => setFontIdx((i) => Math.min(FONT_STEPS.length - 1, i + 1))} className={`${btn} bg-slate-500/70 hover:bg-slate-500`}>A +</button>
          </div>

          {/* Color themes */}
          <div className="flex items-center gap-1" role="group" aria-label="Colour theme">
            {THEMES.map((t) => (
              <button
                key={t.id}
                data-testid={`theme-${t.id}`}
                title={t.label}
                aria-pressed={theme === t.id}
                onClick={() => setTheme(t.id)}
                className={`w-6 h-6 text-xs font-bold rounded-sm border ${t.cls} ${theme === t.id ? "ring-2 ring-white ring-offset-1 ring-offset-[#0e7490]" : ""}`}
              >A</button>
            ))}
          </div>

          <button data-testid="screen-reader-btn" onClick={() => setSrOpen(true)} className={`${chip} hidden lg:inline-flex items-center gap-1.5`}>
            <Eye size={13} /> Screen Reader Access
          </button>
        </div>
      </div>

      {srOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60" onClick={() => setSrOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white text-slate-800 max-w-lg w-full rounded-md p-8 text-left">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl text-[#0e7490]">Screen Reader Access</h3>
              <button data-testid="sr-close" onClick={() => setSrOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">This website is designed to be compatible with popular screen reading software. The following free and commercial screen readers can be used to access this portal:</p>
            <table className="w-full text-sm border border-slate-200">
              <thead className="bg-slate-100 text-slate-600"><tr><th className="text-left px-3 py-2">Screen Reader</th><th className="text-left px-3 py-2">Website</th></tr></thead>
              <tbody className="divide-y divide-slate-200">
                <tr><td className="px-3 py-2">NVDA (Free)</td><td className="px-3 py-2"><a className="text-[#0e7490] underline" href="https://www.nvaccess.org" target="_blank" rel="noreferrer">nvaccess.org</a></td></tr>
                <tr><td className="px-3 py-2">JAWS</td><td className="px-3 py-2"><a className="text-[#0e7490] underline" href="https://www.freedomscientific.com" target="_blank" rel="noreferrer">freedomscientific.com</a></td></tr>
                <tr><td className="px-3 py-2">VoiceOver (macOS/iOS)</td><td className="px-3 py-2"><a className="text-[#0e7490] underline" href="https://www.apple.com/accessibility/vision/" target="_blank" rel="noreferrer">apple.com/accessibility</a></td></tr>
                <tr><td className="px-3 py-2">TalkBack (Android)</td><td className="px-3 py-2"><a className="text-[#0e7490] underline" href="https://support.google.com/accessibility/android" target="_blank" rel="noreferrer">support.google.com</a></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
